import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Circle, Copy, KeyRound, ListChecks, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { OrgTabs } from "@/components/OrgTabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useOrg } from "@/hooks/useOrg";
import { api } from "@/lib/api";

// Standalone sync client (Phase 10c) — same API key, no Claude Code required. `login`
// auto-detects every installed harness, so the commands are identical across them; only
// where artifacts land differs (server matrix: backend/skill_utils.REGISTRY_INSTALL).
const STANDALONE_STEPS = [
  "pipx install runebox-sync   # or: uvx runebox-sync",
  "runebox-sync login",
  "runebox-sync autosync --apply   # re-sync on every new shell",
];

const HARNESS_TABS = [
  {
    id: "claude",
    label: "Claude Code",
    steps: [
      "claude plugin marketplace add runebox-ai/runebox-plugin",
      "claude plugin install runebox",
      "/runebox:login",
    ],
    note: "Skills, agents, commands, and output styles install at their native paths and re-sync quietly at every session start.",
  },
  {
    id: "codex",
    label: "Codex CLI",
    steps: STANDALONE_STEPS,
    note: "Skills land in ~/.codex/skills; commands become Codex prompts in ~/.codex/prompts.",
  },
  {
    id: "cursor",
    label: "Cursor",
    steps: STANDALONE_STEPS,
    note: "Skills land in ~/.cursor/skills; commands in ~/.cursor/commands.",
  },
  {
    id: "gemini",
    label: "Gemini CLI",
    steps: STANDALONE_STEPS,
    note: "Skills land in ~/.gemini/skills. One login covers every harness on the machine.",
  },
];

const copy = (text) => {
  navigator.clipboard.writeText(text);
  toast.success("Copied");
};

function CmdRow({ cmd }) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-lg border border-[color:var(--rb-border)] bg-[color:var(--rb-code)] px-3 py-1.5">
      <code className="rb-mono text-xs text-[color:var(--rb-text)]">{cmd}</code>
      <button onClick={() => copy(cmd)} className="shrink-0 text-[color:var(--rb-text-faint)] hover:text-[color:var(--rb-text)]">
        <Copy className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export default function OrgSetup() {
  const { slug } = useParams();
  const { org } = useOrg(slug);
  const [memberCount, setMemberCount] = useState(null);
  const [catalogCount, setCatalogCount] = useState(null);
  const [keys, setKeys] = useState(null);
  const [keyName, setKeyName] = useState("");
  const [creating, setCreating] = useState(false);
  const [newToken, setNewToken] = useState(null); // shown once, right after creation

  useEffect(() => {
    if (!slug) return;
    api.get(`/orgs/${slug}/members`).then(({ data }) => setMemberCount(data.length)).catch(() => {});
    api.get(`/orgs/${slug}/catalog`).then(({ data }) => setCatalogCount(data.length)).catch(() => {});
  }, [slug]);

  const loadKeys = useCallback(async () => {
    if (!slug) return;
    try {
      const { data } = await api.get(`/orgs/${slug}/keys`);
      setKeys(data);
    } catch (e) {
      setKeys([]);
    }
  }, [slug]);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const createKey = async () => {
    setCreating(true);
    try {
      const { data } = await api.post(`/orgs/${slug}/keys`, { name: keyName.trim() || "API key" });
      setNewToken(data.token);
      setKeyName("");
      loadKeys();
    } catch (e) {
      toast.error("Could not create key");
    } finally {
      setCreating(false);
    }
  };

  const revokeKey = async (id) => {
    try {
      await api.delete(`/orgs/${slug}/keys/${id}`);
      loadKeys();
    } catch (e) {
      toast.error("Could not revoke key");
    }
  };

  const invited = (memberCount ?? 0) > 1; // more than just the owner
  const published = (catalogCount ?? 0) > 0;

  return (
    <AppShell>
      <OrgTabs slug={slug} />
      <div className="mx-auto max-w-2xl">
        <div className="flex items-center gap-2">
          <ListChecks className="h-5 w-5 text-[color:var(--rb-accent)]" />
          <h1 className="rb-display text-2xl font-semibold tracking-tight text-[color:var(--rb-text)]">
            Get set up{org ? ` — ${org.name}` : ""}
          </h1>
        </div>
        <p className="mt-1 text-sm text-[color:var(--rb-text-faint)]">Three steps to a working team registry.</p>

        <div className="mt-6 space-y-3">
          <div className="flex items-start gap-3 rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-4" data-testid="setup-step-invite">
            {invited ? (
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[color:var(--rb-accent)]" />
            ) : (
              <Circle className="mt-0.5 h-5 w-5 shrink-0 text-[color:var(--rb-text-faint)]" />
            )}
            <div className="flex-1">
              <div className="text-sm font-medium text-[color:var(--rb-text)]">Invite your team</div>
              <p className="mt-0.5 text-xs text-[color:var(--rb-text-faint)]">Bring your engineers in — they'll see everything approved for the org.</p>
              <Link to={`/orgs/${slug}/members`} className="mt-2 inline-block text-xs text-[color:var(--rb-accent)] hover:underline">Go to Members →</Link>
            </div>
          </div>

          <div className="flex items-start gap-3 rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-4" data-testid="setup-step-publish">
            {published ? (
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[color:var(--rb-accent)]" />
            ) : (
              <Circle className="mt-0.5 h-5 w-5 shrink-0 text-[color:var(--rb-text-faint)]" />
            )}
            <div className="flex-1">
              <div className="text-sm font-medium text-[color:var(--rb-text)]">Publish your first artifact</div>
              <p className="mt-0.5 text-xs text-[color:var(--rb-text-faint)]">Build or upload a skill, agent, or command for this org's catalog.</p>
              <div className="mt-2 flex gap-3 text-xs">
                <Link to={`/build?org=${slug}`} className="text-[color:var(--rb-accent)] hover:underline">Build with AI →</Link>
                <Link to={`/upload?org=${slug}`} className="text-[color:var(--rb-accent)] hover:underline">Upload →</Link>
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3 rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-4" data-testid="setup-step-plugin">
            {(keys?.length ?? 0) > 0 ? (
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[color:var(--rb-accent)]" />
            ) : (
              <Circle className="mt-0.5 h-5 w-5 shrink-0 text-[color:var(--rb-text-faint)]" />
            )}
            <div className="flex-1">
              <div className="text-sm font-medium text-[color:var(--rb-text)]">Install the sync client</div>
              <p className="mt-0.5 text-xs text-[color:var(--rb-text-faint)]">
                Pick your coding agent — every approved artifact installs and stays synced. Create a key
                below, then paste it when login asks for it.
              </p>
              <Tabs defaultValue="claude" className="mt-2">
                <TabsList className="h-8 bg-[color:var(--rb-code)]">
                  {HARNESS_TABS.map((h) => (
                    <TabsTrigger key={h.id} value={h.id} data-testid={`harness-tab-${h.id}`}
                      className="px-2.5 text-xs data-[state=active]:bg-[color:var(--rb-obsidian)] data-[state=active]:text-[color:var(--rb-text)]">
                      {h.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {HARNESS_TABS.map((h) => (
                  <TabsContent key={h.id} value={h.id}>
                    <div className="space-y-1.5">
                      {h.steps.map((c) => <CmdRow key={c} cmd={c} />)}
                    </div>
                    <p className="mt-1.5 text-[10px] text-[color:var(--rb-text-faint)]">{h.note}</p>
                  </TabsContent>
                ))}
              </Tabs>

              <div className="mt-4 border-t border-[color:var(--rb-border)] pt-3">
                <div className="flex items-center gap-1.5 text-xs font-medium text-[color:var(--rb-text)]">
                  <KeyRound className="h-3.5 w-3.5 text-[color:var(--rb-accent)]" /> API keys
                </div>

                {newToken && (
                  <div className="mt-2 rounded-lg border border-[color:rgba(45,212,191,0.4)] bg-[color:rgba(45,212,191,0.08)] p-2.5" data-testid="new-key-token">
                    <div className="flex items-center justify-between gap-2">
                      <code className="rb-mono truncate text-xs text-[color:var(--rb-text)]">{newToken}</code>
                      <button onClick={() => copy(newToken)} className="shrink-0 text-[color:var(--rb-accent)] hover:text-[color:var(--rb-accent-2)]">
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <p className="mt-1 text-[10px] text-[color:var(--rb-gold)]">Copy this now — it won't be shown again.</p>
                  </div>
                )}

                <div className="mt-2 space-y-1.5">
                  {(keys || []).map((k) => (
                    <div key={k.id} className="flex items-center justify-between gap-2 rounded-lg border border-[color:var(--rb-border)] bg-[color:var(--rb-code)] px-3 py-1.5" data-testid="api-key-row">
                      <div className="min-w-0">
                        <div className="rb-mono truncate text-xs text-[color:var(--rb-text)]">{k.name} · {k.prefix}…</div>
                        <div className="text-[10px] text-[color:var(--rb-text-faint)]">
                          {k.last_used_at ? `last used ${k.last_used_at.slice(0, 10)}` : "never used"}
                        </div>
                      </div>
                      <button onClick={() => revokeKey(k.id)} data-testid="revoke-key-button"
                        className="shrink-0 text-[color:var(--rb-text-faint)] hover:text-[color:#f87171]" title="Revoke">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="mt-2 flex items-center gap-2">
                  <Input value={keyName} onChange={(e) => setKeyName(e.target.value)} placeholder="e.g. laptop" data-testid="new-key-name-input"
                    className="h-8 max-w-[160px] border-[color:var(--rb-border)] bg-[color:var(--rb-code)] text-xs text-[color:var(--rb-text)]" />
                  <Button size="sm" onClick={createKey} disabled={creating} data-testid="create-key-button"
                    className="h-8 rounded-lg bg-[color:var(--rb-accent)] text-xs text-[color:var(--rb-void)] hover:bg-[color:var(--rb-accent-2)]">
                    {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "New key"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
